import { useEffect, useMemo, useState } from 'react';

// Detecção robusta do Word Add-in context.
// Estratégia em camadas:
//  1) window.Office.context.host (canônico)
//  2) Office.onReady() callback/promise
//  3) Polling por window.Word (API específica do Word) por até 5s
//     → essencial em Word Preview / sideload onde onReady pode demorar.
export function useOfficeContext() {
  const [ready, setReady] = useState(false);
  const [host, setHost] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let pollTimer = null;
    const startTs = Date.now();
    const MAX_POLL_MS = 5000;

    const finalize = (nextHost = null) => {
      if (cancelled) return;
      setHost(nextHost ? String(nextHost) : null);
      setReady(true);
      if (pollTimer) {
        try { clearInterval(pollTimer); } catch {}
        pollTimer = null;
      }
    };

    const sniffHost = () => {
      const office = window?.Office;
      const ctxHost = office?.context?.host ? String(office.context.host) : null;
      if (ctxHost) return ctxHost;
      // Fallback: window.Word / window.Excel / window.PowerPoint são populados
      // quando Office.js carrega no respectivo host.
      if (typeof window?.Word !== 'undefined') return 'Word';
      if (typeof window?.Excel !== 'undefined') return 'Excel';
      if (typeof window?.PowerPoint !== 'undefined') return 'PowerPoint';
      return null;
    };

    // Tentativa imediata
    const eager = sniffHost();
    if (eager) {
      finalize(eager);
      return () => { cancelled = true; };
    }

    // Office.onReady (caminho canônico)
    const office = window?.Office;
    if (office && typeof office.onReady === 'function') {
      try {
        const maybePromise = office.onReady((info) => {
          const detected = (info?.host && String(info.host)) || sniffHost();
          if (detected) finalize(detected);
        });
        if (maybePromise && typeof maybePromise.then === 'function') {
          maybePromise
            .then((info) => {
              const detected = (info?.host && String(info.host)) || sniffHost();
              if (detected) finalize(detected);
            })
            .catch(() => {});
        }
      } catch {}
    }

    // Polling fallback — cobre casos onde Office.js carrega tardiamente
    // (Word Preview, sideload manifest, iframe aninhado).
    pollTimer = setInterval(() => {
      if (cancelled) return;
      const detected = sniffHost();
      if (detected) {
        finalize(detected);
        return;
      }
      if (Date.now() - startTs > MAX_POLL_MS) {
        finalize(null);
      }
    }, 200);

    return () => {
      cancelled = true;
      if (pollTimer) {
        try { clearInterval(pollTimer); } catch {}
      }
    };
  }, []);

  const isOffice = Boolean(ready && host);
  const isWord = Boolean(isOffice && host && String(host).toLowerCase() === 'word');

  return useMemo(() => ({ isOffice, isWord, host, ready }), [isOffice, isWord, host, ready]);
}